/**
 * NEXORA AI - Navigation Configuration (Centralized)
 * 
 * Single source of truth for all navigation structure.
 * All future navigation changes must be made here.
 * 
 * @version 2.0 - Sprint 1 Epic 1.2 (Navigation Premium)
 * @author NEXORA AI Team
 * @date 2026-07-29
 */

import {
  LayoutDashboard,
  Package,
  Sparkles,
  Send,
  BarChart3,
  Share2,
  Palette,
  Settings,
  Building2,
  Brain,
  Wand2,
  History,
  Calendar as CalendarIcon,
  Target,
  MessageSquare,
  Rocket,
} from 'lucide-react';

/**
 * Main Navigation Items (Sidebar - 8 items)
 * These are always visible in the sidebar
 */
export const mainNavItems = [
  {
    id: 'dashboard',
    path: '/app/dashboard',
    labelKey: 'common:navigation.dashboard',
    icon: LayoutDashboard,
    testId: 'nav-dashboard',
    priority: 'p0',
  },
  {
    id: 'products',
    path: '/app/products',
    labelKey: 'common:navigation.products',
    icon: Package,
    testId: 'nav-products',
    priority: 'p0',
  },
  {
    id: 'ai-studio',
    path: '/app/ai-studio',
    labelKey: 'common:navigation.ai_studio',
    icon: Sparkles,
    testId: 'nav-ai-studio',
    priority: 'p0',
    // Sub-navigation will be shown contextually when active
    subNav: [
      {
        id: 'ai-profiles',
        path: '/app/ai-studio/profiles',
        labelKey: 'common:navigation.ai_profiles',
        testId: 'subnav-ai-profiles',
      },
      {
        id: 'prompts',
        path: '/app/ai-studio/prompts',
        labelKey: 'common:navigation.prompts',
        testId: 'subnav-prompts',
      },
      {
        id: 'tone-manager',
        path: '/app/ai-studio/tone',
        labelKey: 'common:navigation.tone_manager',
        testId: 'subnav-tone',
      },
      {
        id: 'library',
        path: '/app/ai-studio/library',
        labelKey: 'common:navigation.library',
        testId: 'subnav-library',
      },
      {
        id: 'history',
        path: '/app/ai-studio/history',
        labelKey: 'common:navigation.history',
        testId: 'subnav-history',
      },
    ],
  },
  {
    id: 'publishing',
    path: '/app/publishing',
    labelKey: 'common:navigation.publishing',
    icon: Send,
    testId: 'nav-publishing',
    priority: 'p0',
    // Tabs will be shown in the page itself
    tabs: [
      {
        id: 'create',
        path: '/app/publishing/create',
        labelKey: 'common:navigation.create',
        testId: 'tab-create',
      },
      {
        id: 'schedule',
        path: '/app/publishing/schedule',
        labelKey: 'common:navigation.schedule',
        testId: 'tab-schedule',
      },
      {
        id: 'calendar',
        path: '/app/publishing/calendar',
        labelKey: 'common:navigation.calendar',
        testId: 'tab-calendar',
      },
    ],
  },
  {
    id: 'analytics',
    path: '/app/analytics',
    labelKey: 'common:navigation.analytics',
    icon: BarChart3,
    testId: 'nav-analytics',
    priority: 'p1',
  },
  {
    id: 'social-accounts',
    path: '/app/social-accounts',
    labelKey: 'common:navigation.social_accounts',
    icon: Share2,
    testId: 'nav-social-accounts',
    priority: 'p0',
  },
  {
    id: 'brand-center',
    path: '/app/brand-center',
    labelKey: 'common:navigation.brand_center',
    icon: Palette,
    testId: 'nav-brand-center',
    priority: 'p1',
    subNav: [
      {
        id: 'identity',
        path: '/app/brand-center/identity',
        labelKey: 'common:navigation.identity',
        testId: 'subnav-identity',
      },
      {
        id: 'assets',
        path: '/app/brand-center/assets',
        labelKey: 'common:navigation.assets',
        testId: 'subnav-assets',
      },
      {
        id: 'guidelines',
        path: '/app/brand-center/guidelines',
        labelKey: 'common:navigation.guidelines',
        testId: 'subnav-guidelines',
      },
    ],
  },
  {
    id: 'settings',
    path: '/app/settings',
    labelKey: 'common:navigation.settings',
    icon: Settings,
    testId: 'nav-settings',
    priority: 'p2',
  },
];

/**
 * Secondary Navigation Items (Command Palette - Overflow)
 * Accessible via Cmd+K (Command Palette)
 */
export const secondaryNavItems = [
  {
    id: 'organizations',
    path: '/app/organizations',
    labelKey: 'common:navigation.organizations',
    icon: Building2,
    testId: 'cmd-organizations',
    keywords: ['org', 'team', 'workspace'],
  },
  {
    id: 'brand-intelligence',
    path: '/app/brand-intelligence',
    labelKey: 'common:navigation.brand_intelligence',
    icon: Brain,
    testId: 'cmd-brand-intelligence',
    keywords: ['ai', 'insights', 'analysis'],
  },
  {
    id: 'content-studio',
    path: '/app/content-studio',
    labelKey: 'common:navigation.content_studio',
    icon: Wand2,
    testId: 'cmd-content-studio',
    keywords: ['create', 'content', 'editor'],
  },
  {
    id: 'creative-suite',
    path: '/app/creative-suite',
    labelKey: 'common:navigation.creative_suite',
    icon: Palette,
    testId: 'cmd-creative-suite',
    keywords: ['design', 'graphics', 'visual'],
  },
  {
    id: 'objectives',
    path: '/app/objectives',
    labelKey: 'common:navigation.objectives',
    icon: Target,
    testId: 'cmd-objectives',
    keywords: ['goals', 'targets', 'kpi'],
  },
  {
    id: 'feedback',
    path: '/app/feedback',
    labelKey: 'common:navigation.feedback',
    icon: MessageSquare,
    testId: 'cmd-feedback',
    keywords: ['beta', 'suggestions', 'report'],
  },
  {
    id: 'publishing-hub',
    path: '/app/publishing-hub',
    labelKey: 'common:navigation.publishing_hub',
    icon: Rocket,
    testId: 'cmd-publishing-hub',
    keywords: ['publish', 'posts', 'content'],
  },
];

/**
 * Keyboard Shortcuts
 * Global shortcuts for quick navigation
 */
export const keyboardShortcuts = [
  {
    key: 'cmd+d',
    action: 'navigate',
    target: '/app/dashboard',
    description: 'Go to Dashboard',
  },
  {
    key: 'cmd+p',
    action: 'navigate',
    target: '/app/products',
    description: 'Go to Products',
  },
  {
    key: 'cmd+a',
    action: 'navigate',
    target: '/app/ai-studio',
    description: 'Go to AI Studio',
  },
  {
    key: 'cmd+shift+p',
    action: 'navigate',
    target: '/app/publishing/create',
    description: 'Create Post',
  },
  {
    key: 'cmd+k',
    action: 'open-command-palette',
    description: 'Open Command Palette',
  },
  {
    key: 'cmd+/',
    action: 'toggle-sidebar',
    description: 'Toggle Sidebar',
  },
];

/**
 * Navigation Helpers
 */

/**
 * Get all nav items (main + secondary)
 */
export const getAllNavItems = () => {
  return [...mainNavItems, ...secondaryNavItems];
};

/**
 * Find nav item by ID
 */
export const getNavItemById = (id) => {
  return getAllNavItems().find((item) => item.id === id);
};

/**
 * Find nav item by path
 */
export const getNavItemByPath = (path) => {
  // Check main items first
  let item = mainNavItems.find((item) => {
    if (item.path === path) return true;
    // Check sub-nav
    if (item.subNav) {
      return item.subNav.some((sub) => sub.path === path);
    }
    // Check tabs
    if (item.tabs) {
      return item.tabs.some((tab) => tab.path === path);
    }
    return false;
  });

  if (!item) {
    item = secondaryNavItems.find((item) => item.path === path);
  }

  return item;
};

/**
 * Get active parent item based on current path
 */
export const getActiveParent = (currentPath) => {
  return mainNavItems.find((item) => {
    if (currentPath.startsWith(item.path)) return true;
    if (item.subNav) {
      return item.subNav.some((sub) => currentPath.startsWith(sub.path));
    }
    if (item.tabs) {
      return item.tabs.some((tab) => currentPath.startsWith(tab.path));
    }
    return false;
  });
};

/**
 * Get breadcrumb trail for current path
 */
export const getBreadcrumbs = (currentPath) => {
  const breadcrumbs = [];
  const parent = getActiveParent(currentPath);

  if (parent) {
    breadcrumbs.push({
      labelKey: parent.labelKey,
      path: parent.path,
    });

    // Check sub-nav
    if (parent.subNav) {
      const subItem = parent.subNav.find((sub) =>
        currentPath.startsWith(sub.path)
      );
      if (subItem) {
        breadcrumbs.push({
          labelKey: subItem.labelKey,
          path: subItem.path,
        });
      }
    }

    // Check tabs
    if (parent.tabs) {
      const tabItem = parent.tabs.find((tab) =>
        currentPath.startsWith(tab.path)
      );
      if (tabItem) {
        breadcrumbs.push({
          labelKey: tabItem.labelKey,
          path: tabItem.path,
        });
      }
    }
  }

  return breadcrumbs;
};

/**
 * Navigation Evolution Guidelines
 * 
 * To add a new navigation item:
 * 
 * 1. Determine Priority:
 *    - P0 (Critical): Add to mainNavItems (if < 8 items)
 *    - P1/P2: Add to secondaryNavItems (Command Palette)
 * 
 * 2. Group Logically:
 *    - If related to existing parent, add to subNav or tabs
 *    - If new category, consider creating new parent (but keep mainNavItems ≤ 8)
 * 
 * 3. Update Translations:
 *    - Add labelKey to common.json (navigation section)
 *    - Follow NEXORA_AI_PRODUCT_GLOSSARY.md
 * 
 * 4. Test:
 *    - Verify navigation works
 *    - Verify i18n switch works
 *    - Verify keyboard shortcuts work
 *    - Verify breadcrumbs work
 * 
 * The navigation system is designed to scale without breaking.
 * Main principle: Keep mainNavItems ≤ 8, move secondary items to Command Palette.
 */

export default {
  mainNavItems,
  secondaryNavItems,
  keyboardShortcuts,
  getAllNavItems,
  getNavItemById,
  getNavItemByPath,
  getActiveParent,
  getBreadcrumbs,
};
